/**
 * recordingPersistence — durable draft store for in-progress AI-Scribe recordings.
 *
 * Why this exists (CAPA F131): the live-encounter audio captured by the mini-recorder
 * lives only in volatile memory (`chunksRef`) and is uploaded only on "Stop & Process".
 * A HIPAA idle-timeout logout (or any reload/crash) tears down the page and the entire
 * encounter is lost before it ever reaches the server.
 *
 * IndexedDB is the right home: unlike localStorage/sessionStorage (which the logout path
 * clears in a `beforeunload` handler — see utils/httpInterceptor.tsx), IndexedDB survives
 * `localStorage.clear()`/`sessionStorage.clear()`, reloads, and crashes.
 *
 * Design: each `ondataavailable` blob is persisted as its own row in the `chunks` store
 * (keyed by autoIncrement, indexed by draftId). Appending is a single cheap `add` — we
 * never rewrite a growing array. The blob is reconstructed in chunk order on recovery.
 */

const DB_NAME = 'atman_scribe_drafts'
const DB_VERSION = 1
const DRAFTS_STORE = 'drafts'
const CHUNKS_STORE = 'chunks'

export interface DraftMeta {
  /** Unique id for this recording draft (generated by createDraft). */
  id: string
  patientId: string
  patientRefId: string
  patientName: string
  callType: string
  gender?: string | null
  /** MIME type the chunks were recorded with — needed to reconstruct a valid Blob. */
  mimeType: string
  /** Epoch ms the recording started. */
  startedAt: number
  /** Last known recording duration in seconds (best-effort, updated as it runs). */
  durationSeconds: number
}

interface ChunkRow {
  draftId: string
  /** Monotonic sequence so chunks reconstruct in recording order. */
  seq: number
  blob: Blob
}

/** Open (and lazily migrate) the drafts database. */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this environment'))
      return
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(DRAFTS_STORE)) {
        db.createObjectStore(DRAFTS_STORE, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunks = db.createObjectStore(CHUNKS_STORE, {
          keyPath: 'rowId',
          autoIncrement: true,
        })
        chunks.createIndex('draftId', 'draftId', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/** Promisify a single request inside an existing transaction. */
function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/** Promisify transaction completion. */
function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

function genId(): string {
  // Recording ids never need cryptographic strength; uniqueness within a device is enough.
  const rand = Math.random().toString(36).slice(2, 10)
  return `rec_${Date.now().toString(36)}_${rand}`
}

/** Per-draft sequence counter so persisted chunks reconstruct in recording order. */
const seqByDraft = new Map<string, number>()

/**
 * Create a new draft row and return its id. Call this BEFORE starting the MediaRecorder
 * so the first chunk always has a draft to attach to.
 */
export async function createDraft(
  meta: Omit<DraftMeta, 'id' | 'startedAt' | 'durationSeconds'> &
    Partial<Pick<DraftMeta, 'startedAt' | 'durationSeconds'>>
): Promise<string> {
  const db = await openDb()
  const id = genId()
  const row: DraftMeta = {
    id,
    patientId: meta.patientId,
    patientRefId: meta.patientRefId,
    patientName: meta.patientName,
    callType: meta.callType,
    gender: meta.gender ?? null,
    mimeType: meta.mimeType,
    startedAt: meta.startedAt ?? Date.now(),
    durationSeconds: meta.durationSeconds ?? 0,
  }
  const tx = db.transaction(DRAFTS_STORE, 'readwrite')
  tx.objectStore(DRAFTS_STORE).put(row)
  await txDone(tx)
  seqByDraft.set(id, 0)
  db.close()
  return id
}

/** Append one recorded chunk to a draft. Safe to fire-and-forget per `ondataavailable`. */
export async function appendChunk(draftId: string, blob: Blob): Promise<void> {
  if (!blob || blob.size === 0) return
  const db = await openDb()
  const seq = seqByDraft.get(draftId) ?? 0
  seqByDraft.set(draftId, seq + 1)
  const tx = db.transaction(CHUNKS_STORE, 'readwrite')
  const row: ChunkRow = { draftId, seq, blob }
  tx.objectStore(CHUNKS_STORE).add(row)
  await txDone(tx)
  db.close()
}

/** Update the running duration on a draft (best-effort metadata). */
export async function updateDraftDuration(draftId: string, durationSeconds: number): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(DRAFTS_STORE, 'readwrite')
  const store = tx.objectStore(DRAFTS_STORE)
  const existing = await reqDone(store.get(draftId))
  if (existing) {
    existing.durationSeconds = durationSeconds
    store.put(existing)
  }
  await txDone(tx)
  db.close()
}

/** List all draft metadata, newest first. Does NOT load chunk blobs. */
export async function listDrafts(): Promise<DraftMeta[]> {
  const db = await openDb()
  const tx = db.transaction(DRAFTS_STORE, 'readonly')
  const all = await reqDone(tx.objectStore(DRAFTS_STORE).getAll() as IDBRequest<DraftMeta[]>)
  db.close()
  return (all || []).sort((a, b) => b.startedAt - a.startedAt)
}

/** Reconstruct the full audio Blob for a draft from its persisted chunks, in order. */
export async function getDraftBlob(draftId: string): Promise<Blob | null> {
  const db = await openDb()
  const draftTx = db.transaction(DRAFTS_STORE, 'readonly')
  const meta = await reqDone(
    draftTx.objectStore(DRAFTS_STORE).get(draftId) as IDBRequest<DraftMeta | undefined>
  )
  const chunkTx = db.transaction(CHUNKS_STORE, 'readonly')
  const index = chunkTx.objectStore(CHUNKS_STORE).index('draftId')
  const rows = await reqDone(index.getAll(IDBKeyRange.only(draftId)) as IDBRequest<ChunkRow[]>)
  db.close()
  if (!rows || rows.length === 0) return null
  rows.sort((a, b) => a.seq - b.seq)
  const type = meta?.mimeType || rows[0].blob.type || 'audio/webm'
  return new Blob(rows.map(r => r.blob), { type })
}

/** Delete a draft and all its chunks (on successful upload, or on user discard). */
export async function deleteDraft(draftId: string): Promise<void> {
  const db = await openDb()
  const tx = db.transaction([DRAFTS_STORE, CHUNKS_STORE], 'readwrite')
  tx.objectStore(DRAFTS_STORE).delete(draftId)
  const index = tx.objectStore(CHUNKS_STORE).index('draftId')
  const keysReq = index.getAllKeys(IDBKeyRange.only(draftId)) as IDBRequest<IDBValidKey[]>
  const keys = await reqDone(keysReq)
  const chunkStore = tx.objectStore(CHUNKS_STORE)
  for (const k of keys || []) {
    chunkStore.delete(k)
  }
  await txDone(tx)
  seqByDraft.delete(draftId)
  db.close()
}
